// ForgeInterest — Interest / TAM Dashboard
// Top-of-funnel market analysis: Total Addressable Market → Serviceable Market → Realistic Prospects → Fill Rate.
// Sections: Hero KPIs, Persona Breakdown, Pipeline Flow, Channel Attribution, Campaign ROI Forecast, Strategic Insights.

import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import {
  useAggregates,
  useTypeformLive,
  useCloudflare,
} from "../../hooks/useDashboard";
import {
  useTam as useTamForge,
  usePersonaBreakdown as usePersonaBreakdownForge,
  useChannelAttribution as useChannelAttributionForge,
  useBenchmarks,
} from "../../hooks/useForge";
import { useTheme } from "../../hooks/useTheme";
import { getChartTheme } from "../../lib/chart-theme";
import type {
  TamPersonaRow,
  PersonaBreakdownStage,
  ChannelAttributionRow,
} from "../../hooks/useForge";
import KPICard from "../../components/dashboard/KPICard";
import ChartCard from "../../components/dashboard/ChartCard";
import { LiveIndicator } from "../../components/shared/LiveIndicator";
import BenchmarkIndicator from "../../components/dashboard/BenchmarkIndicator";

// ── Persona constants ─────────────────────────────────────────────────────────

interface PersonaMeta {
  key: string;
  label: string;
  ageRange: string;
  color: string;
  bgClass: string;
  borderClass: string;
  channels: string;
  barrier: string;
  imageUrl: string;
  icon: React.ReactElement;
}

const PERSONAS: PersonaMeta[] = [
  {
    key: "zoe",
    label: "Zoe",
    ageRange: "16–24",
    color: "#8b5cf6",
    bgClass: "bg-purple-500/10",
    borderClass: "border-purple-500/30",
    channels: "TikTok, Instagram",
    barrier: "Time — school/work conflict; parental approval for minors",
    imageUrl: "/images/personas/zoe.jpg",
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    key: "michael",
    label: "Michael",
    ageRange: "25–40",
    color: "#3b82f6",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/30",
    channels: "Facebook, Instagram",
    barrier: "Family/career balance; unsure of time commitment",
    imageUrl: "/images/personas/michael.jpg",
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    key: "xavier",
    label: "Xavier",
    ageRange: "41–56",
    color: "#22c55e",
    bgClass: "bg-green-500/10",
    borderClass: "border-green-500/30",
    channels: "Facebook, NextDoor",
    barrier: "Physical exam hesitancy; concerned about fitness standards",
    imageUrl: "/images/personas/xavier.jpg",
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        <path d="M3.22 12H9.5l1.5-3 2 6 1.5-3.5H21" />
      </svg>
    ),
  },
  {
    key: "barbara",
    label: "Barbara",
    ageRange: "57–66",
    color: "#f59e0b",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/30",
    channels: "Facebook, Direct Mail",
    barrier: "Awareness — doesn't know non-operational roles exist",
    imageUrl: "/images/personas/barbara.jpg",
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

// ── Static TAM fallback data (used when API is unavailable) ───────────────────

// Research-backed TAM numbers from ACS + NFPA benchmarks — docs/kb/market-analysis-22193.md
// 3-ZIP service area (22193 + 22191 + 22192): combined population ~208,000
// TAM = raw 16-65 age cohort; eligible = SAM (interest × physical × time filters); prospects = SOM/yr
const STATIC_TAM: TamPersonaRow[] = [
  {
    persona: "zoe",
    label: "Zoe (16–27)",
    color: "#8b5cf6",
    tam: 35900,
    eligible: 287,
    prospects: 43,
    currentApplicants: 0,
    currentMembers: 0,
    fillRate: 0,
  },
  {
    persona: "michael",
    label: "Michael (28–43)",
    color: "#3b82f6",
    tam: 53100,
    eligible: 239,
    prospects: 36,
    currentApplicants: 0,
    currentMembers: 0,
    fillRate: 0,
  },
  {
    persona: "xavier",
    label: "Xavier (44–59)",
    color: "#22c55e",
    tam: 42500,
    eligible: 199,
    prospects: 24,
    currentApplicants: 0,
    currentMembers: 0,
    fillRate: 0,
  },
  {
    persona: "barbara",
    label: "Barbara (60–65)",
    color: "#f59e0b",
    tam: 10600,
    eligible: 48,
    prospects: 5,
    currentApplicants: 0,
    currentMembers: 0,
    fillRate: 0,
  },
];

// ── Campaign data (from social advertising playbook) ─────────────────────────

interface CampaignBudget {
  persona: string;
  personaKey: string;
  color: string;
  budget: number;
  targetLeadsMin: number;
  targetLeadsMax: number;
  targetCplMin: number;
  targetCplMax: number;
}

const Q2_CAMPAIGN_BUDGETS: CampaignBudget[] = [
  {
    persona: "Zoe (Gen Z)",
    personaKey: "zoe",
    color: "#8b5cf6",
    budget: 3000,
    targetLeadsMin: 50,
    targetLeadsMax: 100,
    targetCplMin: 30,
    targetCplMax: 60,
  },
  {
    persona: "Michael (Millennial)",
    personaKey: "michael",
    color: "#3b82f6",
    budget: 4500,
    targetLeadsMin: 70,
    targetLeadsMax: 130,
    targetCplMin: 35,
    targetCplMax: 65,
  },
  {
    persona: "Xavier (Gen X)",
    personaKey: "xavier",
    color: "#22c55e",
    budget: 3000,
    targetLeadsMin: 40,
    targetLeadsMax: 80,
    targetCplMin: 40,
    targetCplMax: 70,
  },
  {
    persona: "Barbara (Boomer)",
    personaKey: "barbara",
    color: "#f59e0b",
    budget: 1500,
    targetLeadsMin: 20,
    targetLeadsMax: 40,
    targetCplMin: 45,
    targetCplMax: 80,
  },
  {
    persona: "YouTube Awareness",
    personaKey: "unknown",
    color: "#6b7280",
    budget: 4000,
    targetLeadsMin: 0,
    targetLeadsMax: 0,
    targetCplMin: 0,
    targetCplMax: 0,
  },
  {
    persona: "NextDoor Hyperlocal",
    personaKey: "unknown",
    color: "#6b7280",
    budget: 2000,
    targetLeadsMin: 0,
    targetLeadsMax: 0,
    targetCplMin: 0,
    targetCplMax: 0,
  },
  {
    persona: "Google Search",
    personaKey: "unknown",
    color: "#6b7280",
    budget: 1500,
    targetLeadsMin: 0,
    targetLeadsMax: 0,
    targetCplMin: 0,
    targetCplMax: 0,
  },
  {
    persona: "Retention (members)",
    personaKey: "unknown",
    color: "#6b7280",
    budget: 500,
    targetLeadsMin: 0,
    targetLeadsMax: 0,
    targetCplMin: 0,
    targetCplMax: 0,
  },
];

const Q2_TOTAL_BUDGET = Q2_CAMPAIGN_BUDGETS.reduce((s, c) => s + c.budget, 0);
const CAMPAIGN_LAUNCH_DATE = "2026-04-22";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString();
}

function pct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function getPersonaMeta(key: string): PersonaMeta {
  return (
    PERSONAS.find((p) => p.key === key) ?? {
      key,
      label: key,
      ageRange: "",
      color: "#6b7280",
      bgClass: "bg-gray-500/10",
      borderClass: "border-gray-500/30",
      channels: "Various",
      barrier: "Unknown",
      imageUrl: "",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    }
  );
}

// ── Persona card ──────────────────────────────────────────────────────────────

function PersonaCard({ row }: { row: TamPersonaRow }) {
  const meta = getPersonaMeta(row.persona);
  // marketShare: members / eligible (true penetration, always <1)
  const marketSharePct =
    row.marketShare != null
      ? row.marketShare * 100
      : row.eligible > 0
        ? (row.currentMembers / row.eligible) * 100
        : 0;
  // annualCaptureRate: new members this year / annual prospects
  const capturePct =
    row.annualCaptureRate != null ? row.annualCaptureRate * 100 : 0;

  return (
    <div
      className={`glass rounded-2xl p-5 border ${meta.borderClass} flex flex-col gap-4`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3">
        {/* Face image with icon fallback */}
        <div className="shrink-0 relative">
          {meta.imageUrl ? (
            <img
              src={meta.imageUrl}
              alt={meta.label}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-white/20 shadow-lg"
              style={{ "--tw-ring-color": meta.color } as React.CSSProperties}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.style.display = "none";
                const fallback =
                  target.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`w-14 h-14 rounded-full ${meta.bgClass} items-center justify-center shrink-0`}
            style={{
              color: meta.color,
              display: meta.imageUrl ? "none" : "flex",
            }}
          >
            {meta.icon}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-theme-primary leading-tight">
            {meta.label}
          </h3>
          <p className="text-xs text-theme-muted">Age {meta.ageRange}</p>
        </div>
        <div className="ml-auto text-right">
          <p
            className="text-2xl font-bold tabular-nums leading-none"
            style={{ color: meta.color }}
          >
            {fmt(row.tam)}
          </p>
          <p className="text-xs text-theme-muted mt-0.5">TAM</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <p className="text-theme-muted">Eligible (pool)</p>
          <p className="font-semibold tabular-nums text-theme-primary">
            {fmt(row.eligible)}
          </p>
        </div>
        <div>
          <p className="text-theme-muted">Prospects / yr</p>
          <p className="font-semibold tabular-nums text-theme-primary">
            {fmt(row.prospects)}
          </p>
        </div>
        <div>
          <p className="text-theme-muted">Active Applicants</p>
          <p className="font-semibold tabular-nums text-theme-primary">
            {fmt(row.currentApplicants)}
          </p>
        </div>
        <div>
          <p className="text-theme-muted">Current Members</p>
          <p className="font-semibold tabular-nums text-theme-primary">
            {fmt(row.currentMembers)}
          </p>
        </div>
      </div>

      {/* Market penetration bar — members / eligible pool */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="text-theme-muted">
            Market Penetration (members / eligible)
          </span>
          <span className="font-semibold tabular-nums text-theme-primary">
            {pct(marketSharePct)}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-theme-overlay overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(marketSharePct * 10, 100)}%`,
              backgroundColor: meta.color,
            }}
          />
        </div>
      </div>

      {/* Annual capture rate — new members this year / annual prospects */}
      {capturePct > 0 && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-theme-muted">
              Annual Capture (new this yr / prospects)
            </span>
            <span className="font-semibold tabular-nums text-theme-primary">
              {pct(capturePct)}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-theme-overlay overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(capturePct, 100)}%`,
                backgroundColor: meta.color,
              }}
              title={`${row.newMembersThisYear ?? 0} new members this year out of ${row.prospects} annual prospects`}
            />
          </div>
        </div>
      )}

      {/* Barrier + channels */}
      <div className="space-y-1.5 pt-1 border-t border-theme-subtle">
        <div className="text-xs">
          <span className="text-theme-muted">Key barrier: </span>
          <span className="text-theme-secondary">{meta.barrier}</span>
        </div>
        <div className="text-xs">
          <span className="text-theme-muted">Channels: </span>
          <span className="font-medium text-theme-primary">
            {meta.channels}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="glass rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="skeleton w-11 h-11 rounded-xl" />
        <div className="flex-1 space-y-1.5">
          <div className="skeleton h-3.5 w-24 rounded" />
          <div className="skeleton h-3 w-16 rounded" />
        </div>
        <div className="space-y-1">
          <div className="skeleton h-6 w-16 rounded" />
          <div className="skeleton h-3 w-10 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-1">
            <div className="skeleton h-3 w-16 rounded" />
            <div className="skeleton h-4 w-12 rounded" />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="skeleton h-3 w-full rounded" />
        <div className="skeleton h-1.5 w-full rounded-full" />
      </div>
    </div>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string }>;
  label?: string;
}

function StackedBarTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg border border-theme-subtle space-y-1">
      <p className="font-semibold text-theme-primary mb-1.5">{label}</p>
      {[...payload].reverse().map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: p.fill }}
          />
          <span className="text-theme-muted capitalize">{p.name}:</span>
          <span className="font-medium tabular-nums text-theme-primary">
            {p.value}
          </span>
        </div>
      ))}
      <div className="pt-1 border-t border-theme-subtle flex justify-between">
        <span className="text-theme-muted">Total</span>
        <span className="font-semibold tabular-nums text-theme-primary">
          {total}
        </span>
      </div>
    </div>
  );
}

function HorizontalBarTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg border border-theme-subtle space-y-1">
      <p className="font-semibold text-theme-primary mb-1.5">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: p.fill }}
          />
          <span className="text-theme-muted capitalize">{p.name}:</span>
          <span className="font-medium tabular-nums text-theme-primary">
            {p.value}
          </span>
        </div>
      ))}
      {payload.length > 1 && (
        <div className="pt-1 border-t border-theme-subtle flex justify-between">
          <span className="text-theme-muted">Total</span>
          <span className="font-semibold tabular-nums text-theme-primary">
            {total}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ForgeInterest() {
  // Data hooks
  const { data: tamData, isLoading: tamLoading } = useTamForge();
  const { data: breakdownData, isLoading: breakdownLoading } =
    usePersonaBreakdownForge();
  const { data: channelData, isLoading: channelLoading } =
    useChannelAttributionForge();
  const { data: aggregates } = useAggregates();
  const { data: benchmarksData, isLoading: benchmarksLoading } =
    useBenchmarks(300_000);
  // Live API hooks for top-of-funnel section
  const { data: typeformData, isLoading: typeformLoading } = useTypeformLive();
  const { data: cfData, isLoading: cfLoading } = useCloudflare(30);

  // Theme-aware chart colors
  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme === "dark");

  // Pipeline chart view toggle: 'stacked' = stacked area, 'ridgeline' = overlaid lines
  const [pipelineView, setPipelineView] = useState<"stacked" | "ridgeline">(
    "stacked",
  );

  // Resolve TAM rows (fallback to static if API not yet wired)
  const tamRows: TamPersonaRow[] = useMemo(() => {
    if (tamData?.tam && tamData.tam.length > 0) return tamData.tam;
    return STATIC_TAM;
  }, [tamData]);

  // Hero KPI aggregates
  const heroStats = useMemo(() => {
    const totalTam = tamRows.reduce((s, r) => s + r.tam, 0);
    const totalEligible = tamRows.reduce((s, r) => s + r.eligible, 0);
    const totalProspects = tamRows.reduce((s, r) => s + r.prospects, 0);
    const totalMembers = tamRows.reduce((s, r) => s + r.currentMembers, 0);
    const totalNewThisYr = tamRows.reduce(
      (s, r) => s + (r.newMembersThisYear ?? 0),
      0,
    );
    // marketShare: members / total eligible pool — true penetration
    const marketShare =
      totalEligible > 0 ? (totalMembers / totalEligible) * 100 : 0;
    // annualCaptureRate: new members this year / annual prospects
    const annualCaptureRate =
      totalProspects > 0 ? (totalNewThisYr / totalProspects) * 100 : 0;
    return {
      totalTam,
      totalEligible,
      totalProspects,
      totalMembers,
      totalNewThisYr,
      marketShare,
      annualCaptureRate,
    };
  }, [tamRows]);

  // Active member count from aggregates (if available)
  const activeMemberCount = useMemo(() => {
    if (!aggregates?.aggregates) return heroStats.totalMembers;
    const agg = aggregates.aggregates;
    // Try common aggregate key names
    const raw =
      agg["active_members"] ?? agg["activeMembers"] ?? agg["members_active"];
    return typeof raw === "number" ? raw : heroStats.totalMembers;
  }, [aggregates, heroStats.totalMembers]);

  // marketShareActual: active members / total eligible
  const marketShareActual =
    heroStats.totalEligible > 0
      ? (activeMemberCount / heroStats.totalEligible) * 100
      : heroStats.marketShare;

  // Keep fillRateActual as alias for backward compat display
  const _fillRateActual = marketShareActual;

  // Pipeline flow chart data
  const pipelineChartData = useMemo(() => {
    if (!breakdownData?.stages) return [];
    return breakdownData.stages.map((stage: PersonaBreakdownStage) => ({
      stage: stage.stage,
      zoe: stage.personas.zoe ?? 0,
      michael: stage.personas.michael ?? 0,
      xavier: stage.personas.xavier ?? 0,
      barbara: stage.personas.barbara ?? 0,
      unknown: stage.personas.unknown ?? 0,
    }));
  }, [breakdownData]);

  // Channel attribution data
  const channelRows: ChannelAttributionRow[] = useMemo(() => {
    if (!channelData?.channels) return [];
    return [...channelData.channels].sort((a, b) => b.total - a.total);
  }, [channelData]);

  // Conversion rate ranking
  const channelConvRanked = useMemo(() => {
    return [...channelRows]
      .filter((r) => r.total > 0)
      .sort((a, b) => b.conversionRate - a.conversionRate);
  }, [channelRows]);

  // Strategic insights derived from data
  const insights = useMemo(() => {
    const list: Array<{ title: string; body: string; accent: string }> = [];

    // Biggest TAM gap
    const zoeTam = tamRows.find((r) => r.persona === "zoe");
    if (zoeTam && zoeTam.tam > 10000) {
      list.push({
        title: `Biggest TAM gap: Zoe (${fmt(zoeTam.tam)} addressable)`,
        body: `Only ${fmt(zoeTam.currentApplicants + zoeTam.currentMembers)} current pipeline — invest in TikTok/Instagram to capture Gen Z interest.`,
        accent: "#8b5cf6",
      });
    }

    // Highest ROI channel
    if (channelConvRanked.length > 0) {
      const best = channelConvRanked[0];
      list.push({
        title: `Highest ROI channel: ${best.source}`,
        body: `${pct(best.conversionRate * 100)} BOD approval rate (${best.total} total leads). Prioritize budget and effort here.`,
        accent: "#22c55e",
      });
    } else {
      list.push({
        title: "Highest ROI channel: Current Member referrals",
        body: "Member referrals historically convert at 2–3× the rate of digital channels. Formalize the referral program.",
        accent: "#22c55e",
      });
    }

    // Xavier physical drop-off
    const xavier = tamRows.find((r) => r.persona === "xavier");
    if (xavier) {
      list.push({
        title: "Persona at risk: Xavier (41–56) at physical exam stage",
        body: "Gen X prospects show hesitancy around fitness requirements. Consider pre-physical guidance materials and flexible scheduling.",
        accent: "#f59e0b",
      });
    }

    // Q2 campaign readiness
    list.push({
      title: "Q2 FY26 campaign launches April 22",
      body: `$${fmt(Q2_TOTAL_BUDGET)} across 8 campaigns targeting all four personas. Track CPL against $30–$80 benchmarks weekly.`,
      accent: "#3b82f6",
    });

    return list;
  }, [tamRows, channelConvRanked]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-theme-primary tracking-tight">
            Interest / TAM
          </h1>
          <p className="text-sm text-theme-muted mt-1">
            Addressable market analysis for ZIP 22193 (Woodbridge / Dale City) —
            top-of-funnel volunteer recruitment intelligence.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <LiveIndicator lastUpdatedAt={tamLoading ? null : new Date()} />
        </div>
      </div>

      {/* ── Hero KPI row ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total Addressable Market"
          value={tamLoading ? "—" : fmt(heroStats.totalTam)}
          subtitle="Ages 16–65 across ZIPs 22193, 22191, 22192"
          accent="neutral"
          isLoading={tamLoading}
        />
        <KPICard
          label="Serviceable Market"
          value={tamLoading ? "—" : fmt(heroStats.totalEligible)}
          subtitle="Interest × physical × time-availability filters"
          accent="neutral"
          isLoading={tamLoading}
        />
        <KPICard
          label="Realistic Prospects"
          value={tamLoading ? "—" : fmt(heroStats.totalProspects)}
          subtitle="New members convertible per year (SOM)"
          accent="amber"
          isLoading={tamLoading}
        />
        <KPICard
          label="Market Penetration"
          value={tamLoading ? "—" : pct(marketShareActual)}
          subtitle={`${fmt(activeMemberCount)} members / ${fmt(heroStats.totalEligible)} eligible pool`}
          accent={
            marketShareActual >= 5
              ? "green"
              : marketShareActual >= 2
                ? "amber"
                : "red"
          }
          isLoading={tamLoading}
        />
      </div>

      {/* Data source attribution */}
      <p className="text-xs text-theme-muted -mt-4">
        Based on Census ACS 5-Year Estimates 2019–2023 + NFPA per-capita
        benchmarks. Service area: ZIPs 22193, 22191, 22192.
      </p>

      {/* ── Section 0: Top-of-Funnel Web-to-Form Conversion ──────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-theme-primary">
            Top-of-Funnel Web-to-Form Conversion
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Live funnel from dcvfd.org website visitors to volunteer form
            completions — Cloudflare Analytics + Typeform API.
            {(typeformData?.fetchedAt || cfData?.fetchedAt) && (
              <span className="ml-1">
                Last updated:{" "}
                {new Date(
                  typeformData?.fetchedAt ?? cfData?.fetchedAt ?? "",
                ).toLocaleTimeString()}
                .
              </span>
            )}
          </p>
        </div>

        {typeformLoading || cfLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="glass rounded-xl px-4 py-3 border border-theme-subtle flex items-center gap-4"
              >
                <div className="skeleton w-2.5 h-2.5 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="skeleton h-4 w-32 rounded" />
                  <div className="skeleton h-3 w-48 rounded" />
                </div>
                <div className="hidden sm:block w-32 shrink-0">
                  <div className="skeleton h-2 w-full rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          (() => {
            // Determine if either API returned an error or no data
            const tfError =
              !typeformData ||
              typeformData.error ||
              typeformData.totalCompletions === null;
            const cfError = !cfData || cfData.error || !cfData.totals;

            // Real completions from Typeform; real site visitors from Cloudflare (30-day uniques)
            const tfCompletions: number | null = tfError
              ? null
              : (typeformData!.totalCompletions ?? null);
            const tfInsights = typeformData?.insights;

            // Use insights if available, otherwise fall back to completion count only
            const tfStarts: number | null = tfInsights?.starts ?? null;
            const tfViews: number | null = tfInsights?.views ?? null;

            // Cloudflare 30-day totals
            const siteUniques: number | null = cfError
              ? null
              : (cfData!.totals?.totalUniques ?? null);
            const sitePageViews: number | null = cfError
              ? null
              : (cfData!.totals?.totalPageViews ?? null);

            // Build funnel stages — use real data where available, mark as unavailable otherwise
            interface FunnelStage {
              label: string;
              value: number | null;
              note: string;
              color: string;
            }

            const stages: FunnelStage[] = [
              {
                label: "Website Visitors",
                value: siteUniques,
                note: cfError
                  ? "Data unavailable"
                  : `dcvfd.org unique visitors — last 30 days (Cloudflare Analytics)`,
                color: "#3b82f6",
              },
              {
                label: "Page Views",
                value: sitePageViews,
                note: cfError
                  ? "Data unavailable"
                  : "Total page views across all dcvfd.org pages — last 30 days",
                color: "#8b5cf6",
              },
              {
                label: "Typeform Views",
                value: tfViews,
                note:
                  tfViews === null
                    ? "Typeform Insights data loading"
                    : `Total form page loads across all platforms (Typeform Insights)`,
                color: "#f59e0b",
              },
              {
                label: "Typeform Unique Visitors",
                value: tfStarts,
                note:
                  tfStarts === null
                    ? "Typeform Insights data loading"
                    : "Unique visitors who opened the form (Typeform Insights)",
                color: "#f97316",
              },
              {
                label: "Typeform Completions",
                value: tfCompletions,
                note: tfError
                  ? "Data unavailable"
                  : "Total submitted responses — all time (Typeform API)",
                color: "#0E403A",
              },
            ];

            // For relative bar widths, use the first non-null value as the denominator
            const maxValue = stages.find((s) => s.value !== null)?.value ?? 1;

            // Identify biggest opportunity: largest absolute drop between adjacent known stages
            const knownDrops = stages.map((s, i) => {
              if (i >= stages.length - 1) return 0;
              const next = stages[i + 1];
              if (s.value === null || next.value === null) return 0;
              return s.value - next.value;
            });
            const biggestDropIdx = knownDrops.indexOf(Math.max(...knownDrops));

            return (
              <div className="space-y-2">
                {stages.map((stage, i) => {
                  const nextStage = stages[i + 1];
                  const drop =
                    nextStage &&
                    stage.value !== null &&
                    nextStage.value !== null
                      ? stage.value - nextStage.value
                      : null;
                  const dropPct =
                    drop !== null && stage.value && stage.value > 0
                      ? Math.round((drop / stage.value) * 100)
                      : null;
                  const isOpportunity =
                    i === biggestDropIdx && drop !== null && drop > 0;

                  return (
                    <div key={stage.label}>
                      {/* Funnel bar */}
                      <div
                        className={`glass rounded-xl px-4 py-3 border flex items-center gap-4 ${
                          isOpportunity
                            ? "border-amber-500/40 bg-amber-500/5"
                            : "border-theme-subtle"
                        }`}
                      >
                        <div
                          className="w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: stage.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span
                              className="text-sm font-semibold tabular-nums"
                              style={{ color: stage.color }}
                            >
                              {stage.value !== null
                                ? stage.value.toLocaleString()
                                : "—"}
                            </span>
                            <span className="text-sm font-medium text-theme-secondary">
                              {stage.label}
                            </span>
                            {isOpportunity && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                                Highest-impact intervention opportunity
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-theme-muted mt-0.5">
                            {stage.note}
                          </p>
                        </div>
                        {/* Width bar showing relative volume */}
                        <div className="hidden sm:block w-32 shrink-0">
                          <div className="h-2 rounded-full bg-theme-overlay overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width:
                                  stage.value !== null
                                    ? `${Math.round((stage.value / maxValue) * 100)}%`
                                    : "0%",
                                backgroundColor: stage.color,
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Connector arrow with drop-off */}
                      {nextStage && (
                        <div className="flex items-center gap-2 pl-6 py-1">
                          <svg
                            width="10"
                            height="16"
                            viewBox="0 0 10 16"
                            className="shrink-0 opacity-30"
                          >
                            <path
                              d="M5 0 L5 12 L2 9 M5 12 L8 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              fill="none"
                              strokeLinecap="round"
                            />
                          </svg>
                          {drop !== null && dropPct !== null ? (
                            <span
                              className={`text-xs tabular-nums font-medium ${isOpportunity ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-theme-muted"}`}
                            >
                              -{drop.toLocaleString()} ({dropPct}% drop)
                            </span>
                          ) : (
                            <span className="text-xs text-theme-muted">—</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}
      </section>

      {/* ── Section 1: Persona Breakdown ─────────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-theme-primary">
            Persona Breakdown
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Four target audiences — their market size, pipeline presence, and
            key barriers.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {tamLoading
            ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
            : tamRows
                .filter((r) => r.persona !== "unknown")
                .map((row) => <PersonaCard key={row.persona} row={row} />)}
        </div>
      </section>

      {/* ── Section 2: Persona Pipeline Flow ────────────────────────────── */}
      <ChartCard
        title="Persona Pipeline Flow"
        subtitle="Pipeline stages × persona — reveals where each audience drops off"
        minHeight={320}
        headerRight={
          <div className="flex items-center gap-1 p-0.5 rounded-lg bg-theme-overlay border border-theme-subtle">
            <button
              onClick={() => setPipelineView("stacked")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                pipelineView === "stacked"
                  ? "bg-dcvfd text-white shadow-sm"
                  : "text-theme-muted hover:text-theme-primary"
              }`}
            >
              Stacked Area
            </button>
            <button
              onClick={() => setPipelineView("ridgeline")}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                pipelineView === "ridgeline"
                  ? "bg-dcvfd text-white shadow-sm"
                  : "text-theme-muted hover:text-theme-primary"
              }`}
            >
              Ridgeline
            </button>
          </div>
        }
      >
        {breakdownLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="skeleton w-full h-full rounded-lg" />
          </div>
        ) : pipelineChartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center gap-2">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-theme-muted opacity-40"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
            <p className="text-sm text-theme-muted">
              Pipeline breakdown data loading...
            </p>
          </div>
        ) : pipelineView === "stacked" ? (
          /* View A — Stacked Area: shows cumulative flow through stages */
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={pipelineChartData}
              margin={{ top: 4, right: 8, left: -16, bottom: 40 }}
            >
              <defs>
                {PERSONAS.map((p) => (
                  <linearGradient
                    key={p.key}
                    id={`grad-${p.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={p.color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={p.color} stopOpacity={0.3} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={chart.gridStroke}
                vertical={false}
              />
              <XAxis
                dataKey="stage"
                tick={{ fontSize: 10, fill: chart.tickColor }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fontSize: 10, fill: chart.tickColor }} />
              <Tooltip
                content={<StackedBarTooltip />}
                cursor={{ fill: chart.cursorFill }}
              />
              {PERSONAS.map((p) => (
                <Area
                  key={p.key}
                  type="monotone"
                  dataKey={p.key}
                  stackId="a"
                  stroke={p.color}
                  fill={`url(#grad-${p.key})`}
                  name={p.label}
                  strokeWidth={1.5}
                />
              ))}
              <Area
                type="monotone"
                dataKey="unknown"
                stackId="a"
                stroke="#6b7280"
                fill="#6b728040"
                name="Other"
                strokeWidth={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          /* View B — Ridgeline: overlaid transparent areas per persona for shape comparison */
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={pipelineChartData}
              margin={{ top: 4, right: 8, left: -16, bottom: 40 }}
            >
              <defs>
                {PERSONAS.map((p) => (
                  <linearGradient
                    key={p.key}
                    id={`ridge-${p.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={p.color} stopOpacity={0.45} />
                    <stop offset="95%" stopColor={p.color} stopOpacity={0.05} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={chart.gridStroke}
                vertical={false}
              />
              <XAxis
                dataKey="stage"
                tick={{ fontSize: 10, fill: chart.tickColor }}
                angle={-35}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fontSize: 10, fill: chart.tickColor }} />
              <Tooltip
                content={<StackedBarTooltip />}
                cursor={{ fill: chart.cursorFill }}
              />
              {PERSONAS.map((p) => (
                <Area
                  key={p.key}
                  type="monotone"
                  dataKey={p.key}
                  stroke={p.color}
                  fill={`url(#ridge-${p.key})`}
                  name={p.label}
                  strokeWidth={2}
                  dot={{ r: 3, fill: p.color, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}

        {/* Legend */}
        {!breakdownLoading && pipelineChartData.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-theme-subtle">
            {PERSONAS.map((p) => (
              <span
                key={p.key}
                className="inline-flex items-center gap-1.5 text-xs text-theme-muted"
              >
                <span
                  className="w-2.5 h-2.5 rounded-sm"
                  style={{ background: p.color }}
                />
                {p.label} ({p.ageRange})
              </span>
            ))}
            {pipelineView === "stacked" && (
              <span className="inline-flex items-center gap-1.5 text-xs text-theme-muted">
                <span className="w-2.5 h-2.5 rounded-sm bg-gray-500" />
                Other
              </span>
            )}
          </div>
        )}
      </ChartCard>

      {/* ── Section 3: Channel Attribution Analysis ──────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Source Volume by Persona */}
        <ChartCard
          title="Source Volume by Persona"
          subtitle="Which channels attract which audiences"
          minHeight={280}
        >
          {channelLoading ? (
            <div className="skeleton w-full h-56 rounded-lg" />
          ) : channelRows.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-sm text-theme-muted">
              Attribution data loading...
            </div>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={Math.max(channelRows.length * 44, 200)}
            >
              <BarChart
                data={channelRows}
                layout="vertical"
                margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chart.gridStroke}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: chart.tickColor }}
                />
                <YAxis
                  type="category"
                  dataKey="source"
                  tick={{ fontSize: 10, fill: chart.tickColor }}
                  width={110}
                />
                <Tooltip
                  content={<HorizontalBarTooltip />}
                  cursor={{ fill: chart.cursorFill }}
                />
                {PERSONAS.map((p) => (
                  <Bar
                    key={p.key}
                    dataKey={`personas.${p.key}`}
                    stackId="a"
                    fill={p.color}
                    name={p.label}
                  />
                ))}
                <Bar
                  dataKey="personas.unknown"
                  stackId="a"
                  fill="#6b7280"
                  name="Other"
                >
                  <LabelList
                    dataKey="total"
                    position="right"
                    style={{ fontSize: 10, fill: chart.tickColor }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Right: Source Conversion Rate */}
        <ChartCard
          title="Source Conversion Rate"
          subtitle="BOD approval rate by channel — ranked best to worst"
          minHeight={280}
        >
          {channelLoading ? (
            <div className="skeleton w-full h-56 rounded-lg" />
          ) : channelConvRanked.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-sm text-theme-muted">
              Conversion data loading...
            </div>
          ) : (
            <div className="space-y-2.5">
              {channelConvRanked.map((row, i) => {
                const rate = row.conversionRate * 100;
                const color =
                  rate >= 40 ? "#16a34a" : rate >= 20 ? "#d97706" : "#dc2626";
                const bgWidth = Math.min(rate, 100);
                return (
                  <div key={row.source}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-theme-muted w-4 tabular-nums">
                          {i + 1}.
                        </span>
                        <span className="font-medium text-theme-primary">
                          {row.source}
                        </span>
                        <span className="text-theme-muted">
                          ({row.total} leads)
                        </span>
                      </div>
                      <span
                        className="font-bold tabular-nums"
                        style={{ color }}
                      >
                        {pct(rate)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-theme-overlay overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${bgWidth}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>
      </div>

      {/* ── Section 4: Campaign ROI Forecast ────────────────────────────── */}
      <ChartCard
        title="Q2 FY26 Campaign ROI Forecast"
        subtitle={`$${fmt(Q2_TOTAL_BUDGET)} total budget across 8 campaigns (April–June 2026)`}
        headerRight={
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/25">
            Launches {CAMPAIGN_LAUNCH_DATE}
          </span>
        }
        minHeight={300}
      >
        <div className="space-y-6">
          {/* UTM tracking critical warning */}
          <div className="rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 flex items-start gap-3">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-red-500 shrink-0 mt-0.5"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className="text-xs text-red-700 dark:text-red-400">
              <span className="font-bold">
                CRITICAL: No UTM parameters on campaign URLs yet.
              </span>{" "}
              The Q2 FY26 ${fmt(Q2_TOTAL_BUDGET)} ad spend will launch with NO
              source attribution. Add UTM tracking before {CAMPAIGN_LAUNCH_DATE}{" "}
              launch.
            </p>
          </div>

          {/* Campaign pending banner */}
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-3 flex items-center gap-3">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-amber-600 shrink-0"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="text-xs text-amber-700 dark:text-amber-400">
              <span className="font-semibold">Campaign data pending</span> —
              Meta Ads, Google Ads, and NextDoor integrations connect on launch
              ({CAMPAIGN_LAUNCH_DATE}). Budget allocations and targets shown
              below.
            </p>
          </div>

          {/* Budget by campaign table */}
          <div>
            <h3 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-3">
              Budget Allocation & Lead Targets
            </h3>
            <div className="space-y-2">
              {Q2_CAMPAIGN_BUDGETS.filter((c) => c.targetLeadsMax > 0).map(
                (c) => (
                  <div
                    key={c.persona}
                    className="flex items-center gap-x-3 gap-y-0.5 flex-wrap"
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: c.color }}
                    />
                    <span className="text-xs text-theme-primary font-medium flex-1 min-w-[110px] truncate">
                      {c.persona}
                    </span>
                    <span className="text-xs tabular-nums text-theme-muted shrink-0">
                      ${fmt(c.budget)}
                    </span>
                    <span className="text-xs tabular-nums text-theme-secondary shrink-0 hidden sm:inline">
                      {c.targetLeadsMin}–{c.targetLeadsMax} leads
                    </span>
                    <span className="text-xs tabular-nums text-theme-muted shrink-0 hidden sm:inline">
                      CPL ${c.targetCplMin}–${c.targetCplMax}
                    </span>
                  </div>
                ),
              )}

              {/* Awareness / other */}
              <div className="pt-2 mt-2 border-t border-theme-subtle space-y-2">
                {Q2_CAMPAIGN_BUDGETS.filter((c) => c.targetLeadsMax === 0).map(
                  (c) => (
                    <div
                      key={c.persona}
                      className="flex items-center gap-x-3 gap-y-0.5 flex-wrap"
                    >
                      <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-gray-400" />
                      <span className="text-xs text-theme-secondary flex-1 min-w-[110px] truncate">
                        {c.persona}
                      </span>
                      <span className="text-xs tabular-nums text-theme-muted shrink-0">
                        ${fmt(c.budget)}
                      </span>
                      <span className="text-xs text-theme-muted shrink-0 hidden sm:inline">
                        Awareness
                      </span>
                      <span className="text-xs text-theme-muted shrink-0 hidden sm:inline">
                        —
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* Budget bar chart */}
          <div>
            <h3 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-3">
              Budget Distribution
            </h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart
                data={Q2_CAMPAIGN_BUDGETS}
                margin={{ top: 4, right: 8, left: -16, bottom: 50 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={chart.gridStroke}
                  vertical={false}
                />
                <XAxis
                  dataKey="persona"
                  tick={{ fontSize: 9, fill: chart.tickColor }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: chart.tickColor }}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  formatter={(value) => [`$${fmt(Number(value))}`, "Budget"]}
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.tooltipBorder}`,
                    color: chart.tooltipText,
                    borderRadius: "0.5rem",
                    fontSize: 11,
                  }}
                  cursor={{ fill: chart.cursorFill }}
                />
                <Bar dataKey="budget" radius={[4, 4, 0, 0]}>
                  {Q2_CAMPAIGN_BUDGETS.map((c, i) => (
                    <Cell key={i} fill={c.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </ChartCard>

      {/* ── Section 4b: Funnel Benchmarks ───────────────────────────────── */}
      {(() => {
        const aisBench = benchmarksData?.benchmarks?.find(
          (b) => b.metric === "ais_attendance",
        );
        const convBench = benchmarksData?.benchmarks?.find(
          (b) => b.metric === "conversion_rate",
        );
        if (!aisBench && !convBench) return null;
        return (
          <ChartCard
            title="Funnel Benchmarks"
            subtitle="DCVFD attendance and conversion rates vs. national VFD averages · Sources: NVFC, IAFC"
            minHeight={0}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 py-1">
              {aisBench && aisBench.currentValue !== null && (
                <BenchmarkIndicator
                  current={aisBench.currentValue}
                  nationalAverage={aisBench.nationalAverage}
                  topQuartile={aisBench.topQuartile}
                  unit="percent"
                  direction="higher-better"
                  label={aisBench.label}
                  source={aisBench.source}
                  note={aisBench.note}
                  isLoading={benchmarksLoading}
                />
              )}
              {convBench && convBench.currentValue !== null && (
                <BenchmarkIndicator
                  current={convBench.currentValue}
                  nationalAverage={convBench.nationalAverage}
                  topQuartile={convBench.topQuartile}
                  unit="percent"
                  direction="higher-better"
                  label={convBench.label}
                  source={convBench.source}
                  note={convBench.note}
                  isLoading={benchmarksLoading}
                />
              )}
            </div>
            <div className="mt-3 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 px-4 py-3 text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed">
              <span className="font-semibold text-zinc-700 dark:text-zinc-300">
                Research finding:{" "}
              </span>
              Moving AIS attendance from 40% to 65% adds ~15 BOD approvals per
              year at current downstream conversion — the highest single-lever
              improvement available. Personalized outreach within 24 hours of
              application is the top-quartile practice. [NVFC 2023]
            </div>
          </ChartCard>
        );
      })()}

      {/* ── Section 5: Strategic Insights ───────────────────────────────── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-theme-primary">
            Strategic Insights
          </h2>
          <p className="text-xs text-theme-muted mt-0.5">
            Data-driven actions to improve funnel conversion and market
            penetration.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {insights.map((insight, i) => (
            <div
              key={i}
              className="glass rounded-2xl p-4 border-l-4 flex gap-3"
              style={{ borderLeftColor: insight.accent }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  backgroundColor: `${insight.accent}20`,
                  color: insight.accent,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-theme-primary leading-snug">
                  {insight.title}
                </p>
                <p className="text-xs text-theme-muted mt-1 leading-relaxed">
                  {insight.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
